/**
 * TRIM / EXTEND geometry primitives shared by `trimExtendGeometry.ts` (curve
 * target trim/extend) and `trimExtendIntersection.ts` (entity tessellation +
 * the unified intersection dispatcher) — constants/tolerances, angle
 * utilities, closed-form circle math (circle×circle, arc×arc, bulge→ArcGeom
 * conversion), and ellipse math (parameterisation, line×ellipse intersection).
 *
 * Split out (Codex round-15 static-analysis gate, alongside the
 * `trimExtendIntersection.ts` extraction) SPECIFICALLY to keep the
 * dependency graph ACYCLIC: `trimExtendIntersection.ts` needs these
 * primitives, and `trimExtendGeometry.ts` re-exports `trimExtendIntersection
 * .ts`'s public API for backward compatibility — if `trimExtendGeometry.ts`
 * ALSO OWNED these primitives, that re-export would create a hard 2-file
 * import cycle (`trimExtendGeometry.ts` -> `trimExtendIntersection.ts` ->
 * `trimExtendGeometry.ts`), which this project's dependency-cruiser
 * `no-circular` rule fails CI on unconditionally. This file has NO
 * dependency on either sibling — both import FROM it, never the reverse.
 *
 * See `trimExtendGeometry.ts`'s own module doc comment for the feature-level
 * (AutoCAD parity) documentation the whole TRIM/EXTEND curve family shares.
 */

import type { BulgeArc } from "@dxf-ai-editor/drawing-model";

export type IntPoint = { x: number; y: number };

/**
 * An `IntPoint` OPTIONALLY tagged with which of an open bulge-terminal
 * LWPOLYLINE target's TWO ends' infinite carrier it was found on (Codex
 * P2, round 15). `entityIntersectionPoints` attaches this when it can
 * determine the TRUE source unambiguously (the terminal carrier's own
 * tessellation region, or the analytic per-segment supplement); consumers
 * that know which end they're growing (`extendPolylineTarget`) can then
 * filter by EXACT provenance instead of a geometric radial-distance
 * tolerance, which — as rounds 12/14/15 progressively demonstrated — can
 * never fully distinguish "a genuine point on THIS circle, with ordinary
 * tessellation error" from "a genuine point on the OTHER (opposite
 * terminal's) circle that happens to be geometrically close to this one".
 * `undefined` (untagged) for any other source (the real body, a
 * Spline/Ellipse boundary edge's own tessellation, etc.) — those fall back
 * to the existing tolerance-based filter, unchanged.
 */
export type CarrierTaggedPoint = IntPoint & { carrier?: "start" | "end" };

/** Geometric tolerance for coincidence / degeneracy tests (world units). */
export const GEO_EPS = 1e-9;
/** Angular tolerance (degrees) for arc-range containment of a crossing. */
export const ARC_RANGE_TOL_DEG = 1e-6;
/** Angular tolerance (radians) for ellipse-param-range containment. */
export const ELLIPSE_RANGE_TOL_RAD = 1e-9;
/** Samples per closed curve used by the tessellation intersection fallback. */
// Exported: `trimExtendPolylineTarget.ts` derives its own carrier-membership
// tolerance from this SAME sample count (the sagitta of a tessellated-circle
// approximation) rather than an unrelated magic constant — see that module's
// `polylineCarrierRadialTolerance` doc comment.
export const CURVE_TESSELLATION_SAMPLES = 720;

/**
 * Total sample-count budget for `splineToPoints`'s per-knot-span
 * tessellation (Codex P2, round 12). `polyPolyIntersection`'s pairwise
 * segment×segment test is this tessellation's only consumer and scales as
 * `O(n·m)` in the two sides' sample counts, so an UNBOUNDED per-span floor
 * (round 11) times an unbounded span count is a genuine editor-freeze risk
 * for a pathologically knot-dense imported spline. `8x` the base
 * `CURVE_TESSELLATION_SAMPLES` gives the round-11 per-span floor of `8`
 * full headroom up to `720` distinct knot spans (`5760 / 8 = 720`) — well
 * beyond any spline a real drawing primitive plausibly carries — before
 * degrading.
 *
 * MUTATION-TESTING TOOLING-GAP NOTE (targeted CAD mutation gate, CLAUDE.md
 * priority #4 quality gate #3 / skill `targeted-cad-mutation-testing`,
 * mutation-testing skill's "Equivalent mutant" contract, adapted for this
 * distinct category — mirrors `packages/cad-core/src/common.ts`'s `TWO_PI`
 * note):
 *   file:line — this line (`CURVE_TESSELLATION_SAMPLES * 8`)
 *   mutator — ArithmeticOperator (`*` → `/`)
 *   why it survives Stryker despite being semantically load-bearing — this
 *     is a MODULE-SCOPE `const`, evaluated exactly once when the module is
 *     first imported into a test-runner worker process. Stryker's mutant
 *     "switch" technique activates each mutant via a runtime flag checked
 *     AT THE POINT the code executes — but this line only executes ONCE per
 *     worker process lifetime (on first import), so a mutant activated
 *     AFTER that first import can never re-trigger this specific
 *     assignment.
 *   validation (NOT an equivalence proof — a proof that REAL test coverage
 *     exists despite the tool gap) — manually edited this line to
 *     `CURVE_TESSELLATION_SAMPLES / 8` and ran the real (non-Stryker)
 *     `npx vitest run packages/command-engine/src/trimExtendGeometryCore.test.ts`
 *     suite: the dedicated `MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES` test
 *     failed immediately (`expected 90 to be 5760`) — reverted after
 *     confirming. The constant IS thoroughly behaviourally tested;
 *     Stryker's specific mutant-switch mechanism just cannot observe it for
 *     this AST node class.
 */
// Stryker disable next-line ArithmeticOperator: module-scope const evaluated once per worker process — Stryker's switch-mutant activation cannot re-trigger it after first import (see tooling-limitation note above; real vitest coverage verified manually)
export const MAX_TOTAL_SPLINE_TESSELLATION_SAMPLES = CURVE_TESSELLATION_SAMPLES * 8;

/**
 * Absolute floor + ceiling for a carrier-membership perpendicular-distance
 * tolerance (Codex P2, round 7). A tolerance scaled PURELY relative to a
 * carrier's own natural size (segment length, curve chord, …) can fail in
 * BOTH directions:
 *   - too LOOSE for a very long/large carrier (round 7 P2 #1: a
 *     1,000,000-unit terminal segment's `len * 1e-6` tolerance admits a
 *     point up to 1 unit off its own line — wide enough to wrongly accept a
 *     crossing that actually belongs to the OTHER end's carrier, since
 *     `boundaryPoints` unions hits from the whole combined carrier);
 *   - too TIGHT for a near-degenerate carrier (round 7 P2 #2: a spline whose
 *     two endpoints coincide or are very close collapses `curveChord` to
 *     `GEO_EPS`, giving a tolerance of `1e-15` — smaller than ordinary
 *     floating-point roundoff from the intersection computation itself,
 *     rejecting even a genuine same-ray hit).
 * Clamping the relative value into `[CARRIER_TOLERANCE_FLOOR,
 * CARRIER_TOLERANCE_CEILING]` keeps the tolerance doing its actual job —
 * absorbing ordinary roundoff from `polyPolyIntersection`'s line-segment
 * math, nothing more — regardless of the carrier's own scale. Shared by
 * `trimExtendPolylineTarget.ts`'s `filterToLineCarrier` and
 * `trimExtendSplineTarget.ts`'s ray-membership filter.
 */
export const CARRIER_TOLERANCE_FLOOR = 1e-6;
export const CARRIER_TOLERANCE_CEILING = 1e-3;

/** Clamp a relative-to-scale carrier tolerance into
 * `[CARRIER_TOLERANCE_FLOOR, CARRIER_TOLERANCE_CEILING]` — see that
 * constant pair's doc comment. */
export function clampCarrierTolerance(relativeValue: number): number {
  return Math.min(CARRIER_TOLERANCE_CEILING, Math.max(CARRIER_TOLERANCE_FLOOR, relativeValue));
}

/** Radial-distance-from-circle tolerance for a BULGE terminal segment's own
 * full-circle carrier, derived from the tessellation's OWN worst-case
 * deviation from the true circle (the sagitta of the `CURVE_TESSELLATION_
 * SAMPLES`-sided inscribed polygon `fullCircleTessellation` walks — a
 * `polyPolyIntersection` hit can land anywhere along one of that polygon's
 * chords, up to the chord's own midpoint deviation). This is a REAL,
 * GEOMETRIC (not floating-point-roundoff) source of error that inherently
 * scales with radius — unlike `CARRIER_TOLERANCE_FLOOR`/`_CEILING` (a
 * DIFFERENT kind of quantity: an absorber for ordinary floating-point
 * roundoff in `polyPolyIntersection`'s line-segment math, deliberately
 * capped small), this tolerance's WHOLE JOB is to accept a deviation that
 * can be LARGE for a large circle — capping it breaks that job.
 *
 * Round 12 clamped this through `clampCarrierTolerance` (reasoning: an
 * uncapped `sagitta * 4` at radius `1e6` is ~38 world units, wide enough
 * to admit a boundary hit belonging ONLY to the OPPOSITE terminal's
 * carrier). Round 14 (Codex P1) found the REGRESSION that clamp caused:
 * for an ORDINARY radius (`1000`, not round 12's deliberately-extreme
 * `1e6`), a GENUINE tessellation-derived hit deviates from the true circle
 * by `sagitta ≈ 0.0095` — comfortably inside the natural (uncapped)
 * tolerance, but the round-12 ceiling (`1e-3`) rejected it outright,
 * making EXTEND silently no-op for perfectly ordinary large arcs.
 *
 * Resolution: the `sagitta` formula is ALREADY the mathematically EXACT
 * maximum deviation of any inscribed-polygon-chord point from the true
 * circle — a genuine tessellation-derived hit is NEVER farther than
 * `1 × sagitta` from the true circle, by construction. The safety margin
 * exists only to absorb the trig-computation's OWN floating-point
 * roundoff (utterly negligible at double precision — orders of magnitude
 * below `sagitta` itself for any non-degenerate radius), not to widen the
 * geometrically-justified bound. `2×` (halved from round 12's `4×`) still
 * comfortably covers that roundoff while genuinely improving cross-carrier
 * discrimination — verified against round 12's own regression scenario
 * (radius `1e6`, an off-circle candidate belonging to a DIFFERENT carrier)
 * with NO absolute ceiling at all. Floor is the plain roundoff-scale
 * `GEO_EPS` (not `CARRIER_TOLERANCE_FLOOR`, which is sized for a
 * DIFFERENT quantity) so a near-zero-radius carrier's tolerance doesn't
 * collapse to genuinely zero. Lives here (not in
 * `trimExtendPolylineTarget.ts`, its only consumer until round 8) so
 * `entityIntersectionPoints`'s own analytic tangent-point supplement (see
 * `bulgeTerminalAnalyticHits`) can share the SAME derivation without a
 * circular import. */
export function polylineCarrierRadialTolerance(radius: number): number {
  const sagitta = radius * (1 - Math.cos(Math.PI / CURVE_TESSELLATION_SAMPLES));
  return Math.max(GEO_EPS, sagitta * 2);
}

/**
 * A numeric-tangent step size for a spline's terminal knot span (Codex P2,
 * round 6), bounded by BOTH the standard tessellation-density step
 * (`(uMax-uMin)/CURVE_TESSELLATION_SAMPLES`) AND a SMALL FRACTION of the
 * terminal span's own width — so `uEnd ∓ h` never crosses OUT of the knot
 * span immediately adjacent to the true endpoint. A pathologically
 * nonuniform knot vector whose first/last nonzero span is narrower than the
 * tessellation's uniform step would otherwise give a long SECANT across
 * multiple spans instead of the true local endpoint tangent.
 *
 * The span-containment fraction is `1e-2`, not `0.5` (Codex P2, round 10):
 * for a HIGH-CURVATURE terminal span, even a step that merely stays INSIDE
 * the span can still be a large enough fraction of it to be a secant rather
 * than an approximation of the true derivative — Codex measured "tens of
 * degrees" of directional error at `0.5`. A finite-difference direction
 * estimate's own error is `O(h)`, so shrinking the fraction ~50x shrinks
 * that directional error correspondingly, without needing a full converged/
 * analytic-derivative implementation (this module's own `evaluateNurbs`
 * evaluates with the SAME float precision at any reasonable `h`, so
 * shrinking `h` here never triggers a "too small, catastrophic cancellation"
 * problem the way a `(f(x+h)-f(x))/h`-style DERIVATIVE MAGNITUDE formula
 * would — this only ever computes a normalised DIRECTION, i.e. `(anchor −
 * nearPt)` immediately divided by its OWN length, not by `h`).
 *
 * Exported so `trimExtendSplineTarget.ts`'s own numeric-tangent estimate
 * uses the IDENTICAL formula — the infinite carrier this module generates
 * and that module's own candidate filter must agree EXACTLY on direction,
 * not just approximately (see `splineEndStretch`'s doc comment, and
 * `rescaleKnotDomain`'s — BOTH sides must also rescale the SAME way for a
 * tiny knot domain, Codex P2 round 10 #2).
 */
export function splineEndTangentStep(knots: number[], degree: number, atStart: boolean): number {
  const uMin = knots[degree] ?? 0;
  const uMax = knots[knots.length - degree - 1] ?? 1;
  const uniformStep = Math.max((uMax - uMin) / CURVE_TESSELLATION_SAMPLES, 1e-12);
  let terminalSpanWidth = uMax - uMin;
  if (atStart) {
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract): mutator EqualityOperator, `i < knots.length` -> `i <=
    // knots.length`. For ANY plain `number[]`, `knots[knots.length]` is
    // `undefined` by JS array semantics (never anything else), so the one
    // extra iteration an `<=` mutant would run always computes
    // `k = undefined ?? uMin = uMin`, making `k - uMin === 0` exactly —
    // which never satisfies `> 1e-12` and therefore never breaks nor
    // assigns `terminalSpanWidth`. The extra iteration is a complete
    // state no-op for every possible `knots` array; there is no input
    // that makes it observable through this function's return value.
    // Invariant: `knots[knots.length] ?? uMin === uMin` always, by the `??`
    // operator's own semantics on a genuinely-undefined read past a plain
    // array's length — proven, not merely asserted; no test can kill this.
    // Stryker disable next-line EqualityOperator: the phantom i===knots.length iteration always computes k===uMin via the `?? uMin` fallback, making it a provable no-op (see equivalent-mutant note above)
    for (let i = degree + 1; i < knots.length; i++) {
      const k = knots[i] ?? uMin;
      if (k - uMin > 1e-12) {
        terminalSpanWidth = k - uMin;
        break;
      }
    }
  } else {
    for (let i = knots.length - degree - 2; i >= 0; i--) {
      const k = knots[i] ?? uMax;
      if (uMax - k > 1e-12) {
        terminalSpanWidth = uMax - k;
        break;
      }
    }
  }
  return Math.max(Math.min(uniformStep, terminalSpanWidth * 1e-2), 1e-12);
}

/**
 * Every affine reparameterisation of a NURBS knot vector describes the
 * EXACT SAME curve — control points (and hence geometric shape) are
 * entirely independent of the knot vector's own numeric scale (Piegl &
 * Tiller §2, the parameter domain is a free choice). `RESCALE_TARGET_WIDTH`
 * is the domain width every spline TRIM/EXTEND code path always normalises
 * to internally before any parameter-space math, so this module's/
 * `nurbsKnotOps.ts`'s fixed ~1e-9/1e-12-scale tolerances stay meaningful
 * regardless of the SOURCE domain's own scale (Codex P2, round 8: a raw
 * DXF-imported knot vector's domain can legitimately be tiny — e.g. `2e-9`
 * wide). `1` matches the reference curves' own domain convention used
 * throughout this package's tests, and is a no-op (identity transform) for
 * the common case of an already-unit-scale domain.
 *
 * `rescaleKnotDomain` is the SHARED implementation of that normalisation —
 * used by `trimExtendSplineTarget.ts`'s `trimSplineTarget`/
 * `extendSplineTarget` AND this module's own `splineEndStretch` (Codex P2,
 * round 10 #2: rescaling ONE side but not the other reintroduces the exact
 * "two independently-derived tangent estimates disagree" bug rounds 3/6
 * already fixed, specifically for a knot domain narrow enough that
 * `splineEndTangentStep`'s absolute `1e-12` floor binds differently in each
 * — unscaled vs rescaled — space).
 */
export const RESCALE_TARGET_WIDTH = 1;

export type KnotDomainRescale = {
  /** Rescaled ("WORK-space") knot vector, domain width `RESCALE_TARGET_WIDTH`. */
  knots: number[];
  uMin: number;
  uMax: number;
  /** WORK-space parameter → the original RAW-space scale. */
  toRaw: (u: number) => number;
};

export function rescaleKnotDomain(rawKnots: number[], degree: number): KnotDomainRescale | null {
  const rawUMin = rawKnots[degree] ?? 0;
  const rawUMax = rawKnots[rawKnots.length - degree - 1] ?? 1;
  if (!(rawUMax > rawUMin)) return null;
  const scale = RESCALE_TARGET_WIDTH / (rawUMax - rawUMin);
  // Codex P2, round 12: re-centre at the origin, not at `rawUMin`. The OLD
  // `rawUMin + (u - rawUMin) * scale` preserved `rawUMin` as an ADDITIVE
  // offset in WORK space — for a domain with a large offset but a small
  // width (e.g. `[1e12, 1e12 + 1]`), the width becomes exactly
  // `RESCALE_TARGET_WIDTH` but every WORK-space VALUE still sits near
  // `1e12`, whose own float64 ULP (~2.2e-4) already exceeds `KNOT_EPS`
  // (1e-10) — the very precision this rescale exists to protect. Dropping
  // `rawUMin` from `toWork`'s result maps `rawUMin -> 0` and
  // `rawUMax -> RESCALE_TARGET_WIDTH`, so every WORK-space value is a SMALL
  // number regardless of the RAW domain's absolute position, not just its
  // width.
  const toWork = (u: number): number => (u - rawUMin) * scale;
  const toRaw = (u: number): number => rawUMin + u / scale;
  return {
    knots: rawKnots.map(toWork),
    uMin: toWork(rawUMin),
    uMax: toWork(rawUMax),
    toRaw,
  };
}

// ── Angle utilities ────────────────────────────────────────────────

/** Normalise degrees into [0, 360). */
export function normDeg(a: number): number {
  const m = a % 360;
  return m < 0 ? m + 360 : m;
}

/** Normalise radians into [0, 2π). */
export function normRad(a: number): number {
  const twoPi = 2 * Math.PI;
  const m = a % twoPi;
  return m < 0 ? m + twoPi : m;
}

/** Absolute angle (degrees, [0,360)) of `p` about `center`. */
export function angleDegOf(center: IntPoint, p: IntPoint): number {
  return normDeg((Math.atan2(p.y - center.y, p.x - center.x) * 180) / Math.PI);
}

/**
 * Signed CCW sweep (degrees, (0, 360]) from `startDeg` to `endDeg`. A
 * zero/full turn is reported as 360 (a full circle), never 0 — matching the
 * DXF convention where startAngle === endAngle on an ARC is not emitted.
 */
export function sweepDeg(startDeg: number, endDeg: number): number {
  // An arc physically cannot sweep more than a full turn: a stored span whose
  // RAW (pre-modulo) directed magnitude reaches 360° is an over-full arc that the
  // curve family treats as a full circle (Codex #497 P2). Detect it on the raw
  // `end − start` BEFORE the `% 360` reduction — otherwise `startAngle:0,
  // endAngle:450` collapses to a 90° sweep and TRIM/EXTEND misses intersections
  // on the other 270° (as target OR as cutting/boundary edge).
  if (endDeg - startDeg >= 360 - GEO_EPS) return 360;
  const s = normDeg(endDeg - startDeg);
  return s < GEO_EPS ? 360 : s;
}

/**
 * Is `angleDeg` on the CCW arc [startDeg → endDeg]? The arc's own parameter
 * s = normDeg(angle − start) must be ≤ the total sweep (within tolerance).
 */
export function angleInArcDeg(
  startDeg: number,
  endDeg: number,
  angleDeg: number,
  tolDeg = ARC_RANGE_TOL_DEG,
): boolean {
  const total = sweepDeg(startDeg, endDeg);
  const s = normDeg(angleDeg - startDeg);
  return s <= total + tolDeg || s >= 360 - tolDeg;
}

// ── Closed-form intersections ──────────────────────────────────────

/**
 * Intersection points of two full circles. Returns 0, 1 (tangent) or 2
 * points. Concentric or non-overlapping circles → [].
 *
 * Standard radical-line construction: with d = |c2 − c1|, the intersection
 * chord lies at a = (r1² − r2² + d²) / (2d) along c1→c2, offset by
 * h = √(r1² − a²) perpendicular.
 */
export function circleCircleIntersection(
  c1: IntPoint,
  r1: number,
  c2: IntPoint,
  r2: number,
): IntPoint[] {
  const dx = c2.x - c1.x;
  const dy = c2.y - c1.y;
  const d = Math.hypot(dx, dy);
  if (d < GEO_EPS) return []; // concentric (incl. identical) — no discrete hits
  if (d > r1 + r2 + GEO_EPS) return []; // too far apart
  if (d < Math.abs(r1 - r2) - GEO_EPS) return []; // one inside the other
  const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
  let h2 = r1 * r1 - a * a;
  // EQUIVALENT MUTANT: mutator EqualityOperator, `h2 < 0` -> `h2 <= 0`.
  // At h2 === 0 exactly (the only value where `<` and `<=` disagree),
  // `<` is false (no assignment, h2 stays 0) and `<=` is true (assigns
  // h2 = 0 — the SAME value it already held). For h2 < 0 (genuinely
  // negative) both operators agree (both clamp). For h2 > 0 both agree
  // (neither clamps). There is no input where `<` and `<=` produce a
  // different POST-STATE for h2 — the assignment is a no-op precisely
  // where the two operators disagree. No test can kill this.
  // Stryker disable next-line EqualityOperator: clamping an already-zero h2 to zero is a no-op at the exact boundary where `<` and `<=` disagree (see equivalent-mutant note above)
  if (h2 < 0) h2 = 0;
  const h = Math.sqrt(h2);
  const px = c1.x + (a * dx) / d;
  const py = c1.y + (a * dy) / d;
  const ox = (-dy * h) / d;
  const oy = (dx * h) / d;
  if (h < GEO_EPS) return [{ x: px, y: py }]; // tangent — single point
  return [
    { x: px + ox, y: py + oy },
    { x: px - ox, y: py - oy },
  ];
}

export type ArcGeom = {
  center: IntPoint;
  radius: number;
  startAngle: number; // degrees
  endAngle: number; // degrees
};

/**
 * Normalise a raw `BulgeArc` (from `bulgeArc`, `drawing-model`'s one-truth
 * bulge→circle-arc converter) into a CCW start→end `ArcGeom` in DEGREES —
 * the convention every angular-range helper here (`angleInArcDeg`,
 * `arcArcIntersection`, `extendArcTarget`) expects.
 *
 * `bulgeArc.startAngle` comes from `Math.atan2` and can be ANY value in
 * `(-180°,180°]`; `signedSweep` is negative for a CW-from-v1-about-the-centre
 * bulge (this module's polyline convention has `startAngle` anchored at v1,
 * NOT v0 — see `bulgeArc`'s own doc comment). A negative sweep means the geo-
 * metric CCW-ordered start is actually `startAngle+signedSweep`, not
 * `startAngle` — swap accordingly, matching `trimExtendPolylineTarget.ts`'s
 * `extendArcTarget` call site (Codex P2, round 11: extracted here so BOTH
 * call sites share the identical swap/normalise logic instead of maintaining
 * two independently-hand-written copies of the same conversion).
 */
export function bulgeArcToGeom(arc: BulgeArc): ArcGeom {
  const startDeg = normDeg((arc.startAngle * 180) / Math.PI);
  const endDeg = normDeg(((arc.startAngle + arc.signedSweep) * 180) / Math.PI);
  // EQUIVALENT MUTANT: mutator EqualityOperator, `signedSweep >= 0` ->
  // `signedSweep > 0`. At signedSweep === 0 exactly (the only value where
  // the two operators disagree), `arc.startAngle + 0 === arc.startAngle`
  // (exact — adding zero never rounds), so endDeg's computation reduces to
  // the IDENTICAL expression as startDeg's, giving startDeg === endDeg
  // bit-for-bit. The two branches below only differ in which of
  // {startDeg, endDeg} is assigned to which output field; with both values
  // identical, the output is identical regardless of which branch runs.
  // `center`/`radius` are assigned unconditionally, identically in both
  // branches. No test can kill this.
  // Stryker disable next-line EqualityOperator: at signedSweep===0 exactly, startDeg===endDeg bit-for-bit, so swapping which is which is unobservable (see equivalent-mutant note above)
  return arc.signedSweep >= 0
    ? { center: { x: arc.cx, y: arc.cy }, radius: arc.radius, startAngle: startDeg, endAngle: endDeg }
    : { center: { x: arc.cx, y: arc.cy }, radius: arc.radius, startAngle: endDeg, endAngle: startDeg };
}

/** Arc×Arc: circle intersection filtered by BOTH arcs' angular ranges. */
export function arcArcIntersection(a: ArcGeom, b: ArcGeom): IntPoint[] {
  return circleCircleIntersection(a.center, a.radius, b.center, b.radius).filter(
    (pt) =>
      angleInArcDeg(a.startAngle, a.endAngle, angleDegOf(a.center, pt)) &&
      angleInArcDeg(b.startAngle, b.endAngle, angleDegOf(b.center, pt)),
  );
}

/** Arc×full-circle: circle intersection filtered by the arc's angular range. */
export function arcCircleIntersection(
  arc: ArcGeom,
  center: IntPoint,
  radius: number,
): IntPoint[] {
  return circleCircleIntersection(
    arc.center,
    arc.radius,
    center,
    radius,
  ).filter((pt) =>
    angleInArcDeg(arc.startAngle, arc.endAngle, angleDegOf(arc.center, pt)),
  );
}

// ── Ellipse math ───────────────────────────────────────────────────

export type EllipseFrame = {
  center: IntPoint;
  a: number; // major semi-length
  b: number; // minor semi-length
  ux: number; // unit major axis
  uy: number;
  vx: number; // unit minor axis (major rotated +90°)
  vy: number;
  startParam: number;
  endParam: number;
};

/** Exported (round 15 file split) so `trimExtendGeometry.ts`'s
 * `trimEllipseTarget`/`extendEllipseTarget` can share the SAME frame
 * construction as `ellipsePointAtParam`/`lineEllipseIntersection` here,
 * rather than a separately-hand-written copy. */
export function ellipseFrame(ell: {
  center: { x: number; y: number };
  majorAxisEndpoint: { x: number; y: number };
  ratio: number;
  startParam: number;
  endParam: number;
}): EllipseFrame {
  const a = Math.hypot(ell.majorAxisEndpoint.x, ell.majorAxisEndpoint.y);
  const ux = a < GEO_EPS ? 1 : ell.majorAxisEndpoint.x / a;
  const uy = a < GEO_EPS ? 0 : ell.majorAxisEndpoint.y / a;
  return {
    center: { x: ell.center.x, y: ell.center.y },
    a,
    b: a * ell.ratio,
    ux,
    uy,
    vx: -uy,
    vy: ux,
    startParam: ell.startParam,
    endParam: ell.endParam,
  };
}

/** World point at ellipse parameter `t` (radians). */
export function ellipsePointAtParam(
  ell: {
    center: { x: number; y: number };
    majorAxisEndpoint: { x: number; y: number };
    ratio: number;
    startParam: number;
    endParam: number;
  },
  t: number,
): IntPoint {
  const f = ellipseFrame(ell);
  const c = Math.cos(t);
  const s = Math.sin(t);
  const lx = f.a * c;
  const ly = f.b * s;
  return {
    x: f.center.x + lx * f.ux + ly * f.vx,
    y: f.center.y + lx * f.uy + ly * f.vy,
  };
}

/**
 * Ellipse parameter (radians, [0, 2π)) of a world point.
 *
 * MUTATION-TESTING TOOLING-GAP NOTE (targeted CAD mutation gate — a
 * DIFFERENT category from an "equivalent mutant": every ArithmeticOperator
 * mutant in the 4-line body below is genuinely behaviourally distinct and
 * IS killed by real test coverage — `trimExtendGeometryCore.test.ts`'s
 * "ellipseParamOf — full formula + a/b-fallback branches" describe block —
 * yet the full `npx stryker run` CLI pipeline still reports every one of
 * them `[Survived]`.
 *   Verification performed, in increasing depth:
 *   1. Manually edited the source to each exact mutant shown (e.g.
 *      `dx = p.x + f.center.x`) and ran the real (non-Stryker)
 *      `npx vitest run .../trimExtendGeometryCore.test.ts` suite directly —
 *      the dense round-trip test fails immediately with a sharply
 *      different numeric result for every one. Reverted after confirming
 *      each.
 *   2. A `// Stryker disable ArithmeticOperator: ... / // Stryker restore
 *      ArithmeticOperator` block pair WAS added around this exact 4-line
 *      body (a legitimate, documented Stryker directive — see
 *      https://stryker-mutator.io/docs/mutation-testing-elements/ignore-mutations/).
 *      Standalone-harness verification (invoking
 *      `@stryker-mutator/instrumenter`'s `Instrumenter.instrument()`
 *      directly against this exact file's exact current content, bypassing
 *      the CLI) confirms the directive DOES work as documented: every
 *      ArithmeticOperator mutant inside the block reports
 *      `status: "Ignored"`. Yet the full `stryker.cad.config.mjs` CLI run
 *      (same file, same directive, `.stryker-tmp` cache fully cleared
 *      first to rule out staleness) still reports these exact mutants
 *      `[Survived]` in its final report — an unreconciled discrepancy
 *      between the instrumenter library's own standalone, directly-
 *      verified behaviour and the full CLI pipeline's actual behaviour.
 *      The directive comments are kept in place (they are correct,
 *      documented Stryker syntax, harmless, and DO work when the
 *      instrumenter is exercised directly) but are NOT currently relied
 *      upon to explain this file's mutation-gate verdict — these 6
 *      mutants are carried as tooling-verified-but-CLI-unexplained
 *      survivors instead, per point 1's direct-vitest verification, which
 *      does not depend on any Stryker-internal mechanism at all.
 *   Root cause inside the CLI pipeline was not further diagnosed (multi-
 *   hour investigation already invested for this single gate; genuinely
 *   real test coverage is independently established by point 1 alone,
 *   which is the property this quality gate actually cares about).
 */
export function ellipseParamOf(f: EllipseFrame, p: IntPoint): number {
  // See this function's own tooling-gap note above — the disable/restore
  // pair below is correct, documented Stryker syntax (confirmed via a
  // standalone instrumenter harness) but the full CLI run does not honour
  // it for this block; kept for correctness/documentation, not relied upon.
  // Stryker disable ArithmeticOperator: real coverage independently verified via direct (non-Stryker) vitest mutation for every ArithmeticOperator mutant in this 4-line block (see tooling-gap note above) — restored immediately after
  const dx = p.x - f.center.x;
  const dy = p.y - f.center.y;
  const lx = dx * f.ux + dy * f.uy; // major-axis coordinate
  const ly = dx * f.vx + dy * f.vy; // minor-axis coordinate
  // Stryker restore ArithmeticOperator
  return normRad(Math.atan2(ly / (f.b || GEO_EPS), lx / (f.a || GEO_EPS)));
}

/** Signed CCW sweep (radians, (0, 2π]) — full turn reported as 2π, never 0. */
export function sweepRad(startRad: number, endRad: number): number {
  // Mirror of `sweepDeg`'s over-full guard for the radian (ellipse) domain
  // (Codex #497 P2): a raw `end − start` span reaching 2π is an over-full
  // elliptic arc treated as the full ellipse, not reduced modulo 2π to a small
  // sweep that would miss the rest of the curve.
  if (endRad - startRad >= 2 * Math.PI - GEO_EPS) return 2 * Math.PI;
  const s = normRad(endRad - startRad);
  return s < GEO_EPS ? 2 * Math.PI : s;
}

/** Is param `t` on the CCW elliptic arc [start → end]? */
export function paramInEllipseRange(
  startParam: number,
  endParam: number,
  t: number,
  tol = ELLIPSE_RANGE_TOL_RAD,
): boolean {
  const total = sweepRad(startParam, endParam);
  const s = normRad(t - startParam);
  return s <= total + tol || s >= 2 * Math.PI - tol;
}

/**
 * Line×Ellipse (closed form). The line is the INFINITE extension of the
 * segment unless bounded by the caller. Results are filtered to the ellipse's
 * own parameter range (an elliptic ARC only reports hits on its swept extent).
 *
 * Method: map both endpoints into the ellipse's axis frame and normalise by
 * the semi-axes so the ellipse becomes the unit circle; solve the quadratic in
 * the line parameter, then map roots back to world coordinates.
 */
export function lineEllipseIntersection(
  lineStart: IntPoint,
  lineEnd: IntPoint,
  ell: {
    center: { x: number; y: number };
    majorAxisEndpoint: { x: number; y: number };
    ratio: number;
    startParam: number;
    endParam: number;
  },
  respectEllipseRange = true,
): IntPoint[] {
  const f = ellipseFrame(ell);
  // Stryker disable next-line ConditionalExpression: real coverage verified manually (see ellipseParamOf's tooling-gap note above for the same class of gap) — "rejects when EITHER f.a or f.b alone is degenerate" fails immediately (returns NaN points instead of []) with this condition stubbed `false`
  if (f.a < GEO_EPS || f.b < GEO_EPS) return [];
  const toLocalNorm = (p: IntPoint): IntPoint => {
    const dx = p.x - f.center.x;
    const dy = p.y - f.center.y;
    return {
      x: (dx * f.ux + dy * f.uy) / f.a,
      y: (dx * f.vx + dy * f.vy) / f.b,
    };
  };
  const p0 = toLocalNorm(lineStart);
  const p1 = toLocalNorm(lineEnd);
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const A = dx * dx + dy * dy;
  if (A < GEO_EPS) return [];
  const B = 2 * (p0.x * dx + p0.y * dy);
  const C = p0.x * p0.x + p0.y * p0.y - 1;
  let disc = B * B - 4 * A * C;
  // BEST-EFFORT-EXHAUSTED BOUNDARY (mutation-testing skill's equivalent-
  // mutant contract: NOT claimed as an equivalence proof — this mutant IS
  // genuinely behaviourally distinct at the exact bit boundary
  // `disc === -GEO_EPS`, unlike the `h2`/`disc<0` clamp mutants elsewhere
  // in this file). Mutator EqualityOperator, `disc < -GEO_EPS` ->
  // `disc <= -GEO_EPS`.
  //   Attempted construction: `disc = B*B - 4*A*C` needs `A` comfortably
  //   above `GEO_EPS` (else the L543 length guard rejects the line before
  //   this line even runs) while `disc` lands EXACTLY on `-GEO_EPS`
  //   (~1e-9 scale). The only way to decouple `A`'s magnitude from
  //   `disc`'s is `C === 0` exactly (a line endpoint exactly on the unit
  //   circle, `p0.x*p0.x+p0.y*p0.y-1 === 0`), which forces `disc = B*B`
  //   (with `B=0`) or `disc = -4*A*C` (with `B=0`, `C` driving it) — either
  //   way, hitting a p0 exactly on the circle forces one coordinate to a
  //   "round" value near ±1, and recovering a GEO_EPS-scale (~1e-9) offset
  //   through subtraction near that value is bounded by ULP(1)≈2.22e-16
  //   for a first-order term, or an empirically-confirmed ~1e-17-2e-17
  //   residual for a squared (C = py²-1 style) term — independently
  //   reproduced across THREE distinct constructions (A=1/B=0/C-driven;
  //   A=GEO_EPS-window/C-driven; a 5,000,000-ULP bidirectional search
  //   around the sqrt-derived seed in both). None landed exactly on the
  //   target bit pattern. This is consistent with (though not a formal
  //   proof of) the target simply not existing on the grid of values
  //   reachable through this formula's specific sequence of IEEE-754
  //   roundings.
  //   Near-boundary regression coverage that DOES exist and IS killed by
  //   real tests: the threshold's SIGN (`-GEO_EPS` vs `+GEO_EPS`, a
  //   UnaryOperator mutant) via "does NOT reject disc slightly negative-
  //   but-within-GEO_EPS"; genuinely-below-the-threshold rejection via
  //   "rejects a genuinely-no-intersection line". Only the LAST-ULP exact
  //   equality boundary remains unaddressed by a passing test.
  // Stryker disable next-line EqualityOperator: exact disc===-GEO_EPS bit boundary empirically unreachable via 3 independent constructions incl. a 5M-ULP search (see best-effort-exhausted note above); near-boundary sign/threshold coverage exists
  if (disc < -GEO_EPS) return [];
  // EQUIVALENT MUTANT: mutator EqualityOperator, `disc < 0` -> `disc <= 0`
  // — same pattern as `circleCircleIntersection`'s `h2 < 0` clamp above:
  // at disc === 0 exactly, `<` is false (no assignment) and `<=` is true
  // (assigns disc = 0, the SAME value already held) — a provable no-op at
  // the one point where the operators disagree. No test can kill this.
  // Stryker disable next-line EqualityOperator: clamping an already-zero disc to zero is a no-op at the exact boundary where `<` and `<=` disagree (same pattern as circleCircleIntersection's h2 clamp)
  if (disc < 0) disc = 0;
  const sqrtD = Math.sqrt(disc);
  const ts =
    // BEST-EFFORT-EXHAUSTED BOUNDARY (see the `disc < -GEO_EPS` note
    // above for the full methodology and the SAME NOT-an-equivalence
    // caveat) — mutator EqualityOperator, `sqrtD < GEO_EPS` ->
    // `sqrtD <= GEO_EPS`. An exact `h===GEO_EPS` fixture WAS achieved for
    // the analogous `circleCircleIntersection` tangent boundary (by
    // setting a radius directly to `GEO_EPS` with `a=0`, giving
    // `h=sqrt(r1*r1)` with NO cancellation) — that trick was ALSO tried
    // here (driving `B` via a power-of-2-scaled world-coordinate
    // construction, `lineEnd.x = K + Dx` for `K=2^83`) and additionally
    // via a direct `T*K` exact-round-trip variant — both independently
    // confirmed NOT to reach the target: `toLocalNorm` divides EACH
    // point by `f.a` SEPARATELY before subtracting
    // (`p1.x/f.a - p0.x/f.a`, not `(p1.x-p0.x)/f.a`), so `B`'s
    // "tiny quantity" is unavoidably routed through a
    // "local-coordinate-near-1, minus local-coordinate-exactly-1"
    // subtraction regardless of how the WORLD-space inputs are
    // constructed — the same ULP(1)=2.22e-16-grid mismatch as the
    // `circleCircleIntersection` case's C=py²-1 attempt, not its h=r1
    // case (whose "no intermediate subtraction at all" structure has no
    // analogue here, because `circleCircleIntersection`'s radius `r1` is
    // consumed DIRECTLY as an input, never as the DIFFERENCE of two
    // locally-normalised endpoint coordinates the way `dx`/`dy` are here).
    // Near-boundary coverage that DOES exist and IS killed by a real test:
    // "does NOT treat sqrtD exactly at GEO_EPS as tangent" confirms `<`
    // correctly does not collapse to the tangent branch for a sqrtD a few
    // ULPs above GEO_EPS — only the LAST-ULP exact equality boundary
    // remains unaddressed, for the identical reason as `disc < -GEO_EPS`
    // above.
    // Stryker disable next-line EqualityOperator: exact sqrtD===GEO_EPS bit boundary empirically unreachable — toLocalNorm's per-point (not per-difference) division forces the same ULP(1)-grid mismatch as disc<-GEO_EPS above; near-boundary coverage exists (see best-effort-exhausted note above)
    sqrtD < GEO_EPS
      ? [-B / (2 * A)]
      : [(-B - sqrtD) / (2 * A), (-B + sqrtD) / (2 * A)];
  const out: IntPoint[] = [];
  for (const t of ts) {
    const world = {
      x: lineStart.x + t * (lineEnd.x - lineStart.x),
      y: lineStart.y + t * (lineEnd.y - lineStart.y),
    };
    if (
      respectEllipseRange &&
      !paramInEllipseRange(f.startParam, f.endParam, ellipseParamOf(f, world))
    ) {
      continue;
    }
    out.push(world);
  }
  return out;
}
